import { ChartColumn, Cpu } from 'lucide-react';
import { ProfileType } from '@ixicare/resources';
import { t } from '~/i18n';
import routes from './routes';
import { MenuRow, MenuRoute } from '@ixicare/ui';

// Sidebar IA (see design_handoff_sidebar_ia): admins get the global items
// (Sites, Statistics) up top, then a site header opening the site-scoped tools.
// Single-site users get the same site tools with Settings at the bottom.
export const buildMenu = (isAdmin: boolean): MenuRow[] => {
  const dashboard: MenuRoute = {
    label: t('menu.dashboard.dashboard'),
    icon: 'dashboard',
    route: routes.dashboard,
  };
  const configuration: MenuRoute = {
    label: t('menu.dashboard.configuration'),
    icon: 'settings',
    route: routes.configuration,
  };
  const alarms: MenuRow[] = [
    { section: t('menu.dashboard.alerts'), accent: 'site' },
    { label: t('menu.dashboard.overview'), icon: 'alarm', route: routes.alerts },
    { label: t('menu.dashboard.residents'), icon: 'user', route: routes.residents },
    {
      label: t('menu.dashboard.healthcareProviders'),
      icon: 'healthcare-providers',
      route: routes.healthcareProviders,
    },
    {
      label: t('menu.dashboard.hcpGroups'),
      icon: 'healthcare-provider-groups',
      route: routes.hcpGroups,
    },
  ];
  const devices: MenuRow[] = [
    { section: t('alerts.status.devices'), accent: 'site' },
    {
      label: t('menu.dashboard.overview'),
      image: '/assets/wristband_distress_button@2x.png',
      route: routes.deviceOverview,
      permissions: ['can_view_site'],
    },
    {
      label: t('alerts.table.status'),
      icon: 'heart-pulse',
      route: routes.deviceHealth,
      permissions: ['can_view_site'],
    },
    {
      label: t('menu.dashboard.firmware'),
      lucide: Cpu,
      route: routes.firmware,
      permissions: ['can_manage_firmware'],
      profileType: [ProfileType.WatcherrEmployee],
    },
  ];

  if (!isAdmin) {
    return [dashboard, ...alarms, ...devices, { divider: true }, configuration];
  }

  return [
    { section: t('menu.dashboard.sectionGeneral'), accent: 'primary' },
    {
      label: t('menu.dashboard.settingsSites'),
      icon: 'residence-map',
      route: routes.sites,
      permissions: ['can_view_site'],
    },
    {
      label: t('menu.dashboard.statistics'),
      lucide: ChartColumn,
      route: routes.statistics,
      permissions: ['can_view_billing'],
    },
    { divider: true },
    { siteHeader: true, eyebrow: t('deviceDetail.site'), siteName: '' },
    dashboard,
    configuration,
    ...alarms,
    ...devices,
  ];
};

// Drops section labels whose items were all filtered out, then dividers left
// dangling at the edges or doubled up.
export const pruneMenu = (rows: MenuRow[]): MenuRow[] => {
  const sectioned = rows.filter((row, i, all) => {
    if (!('section' in row)) return true;
    const next = all[i + 1];
    return !!next && 'route' in next;
  });
  return sectioned.filter((row, i, all) => {
    if (!('divider' in row)) return true;
    const prev = all[i - 1];
    return !!prev && !('divider' in prev) && i < all.length - 1;
  });
};
